/* eslint-disable no-unused-expressions */
import React, {
    useReducer, useRef, useEffect, useContext,
} from 'react';
import Container from 'components/widgets/Container';
import {
    Grid, Chip, makeStyles, Dialog, DialogContent,
    AppBar, Toolbar, IconButton, Typography, useTheme,
    useMediaQuery, Button as ButtonMaterial,
} from '@material-ui/core';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import LoadingMask from 'components/widgets/LoadingMask';
import AccountingStyles from 'styles/modules/accounting/AccountingStyles';
import Split from 'react-split';
import { SaveIcon, CloseIcon } from 'components/icons';
import {
    Col, Form, InputGroup, Button,
} from 'react-bootstrap';
import { isValidField, isValidSchema, getErrorMessage } from 'utils/schema/utils';
import PartsTicketSchema from 'utils/schema/parts/PartsTicketSchema';
import { useLazyQuery, useMutation } from '@apollo/client';
import DropdownQuery from 'components/widgets/DropdownQuery';
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import PartsMapper from 'services/mapData/PartsMapper';
import ModalUtils from 'utils/ModalUtils';
import UserContext from 'components/context/UserContext';
import PartsTicketReducer, { ACTION_TYPES } from 'components/modules/purchaseParts/reducer/PartsTicketReducer';
import { useParams, useHistory, Link as RouteLink } from 'react-router-dom';
import { modules } from 'utils/enum/modules';
import { FetchPolicy } from 'utils/enum/Core';
import DialogActionMessage from 'components/widgets/DialogActionMessage';
import CompanySettingCategory, { CompanySettingkey } from 'utils/enum/CompanySettingEnum';
import { PartsPurchaseSubModules, PartsTicketSalesType, PartsTicketStatus } from 'utils/enum/PartsPurchaseEnum';
import PartsQuery from 'services/graphQL/query/parts/PartsQuery';
import InputNumber from 'components/widgets/InputNumber';
import UserQuery from 'services/graphQL/query/UserQuery';
import PartsTicketDetail from 'components/modules/purchaseParts/partsTicket/create/PartsTicketDetail';
import PartsMutation from 'services/graphQL/mutate/parts/PartsMutation';
import usePartsTicketValidateActions from 'components/modules/purchaseParts/hooks/usePartsTicketValidateActions';
import LabelMemorize from 'components/widgets/form/LabelMemorize';
import If from 'components/widgets/conditional/If';
import CustomerForServiceList from 'components/modules/service/serviceInvoice/create/CustomerForServiceList';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import CreateCustomer from 'components/modules/deals/create/dealTab/CreateCustomer';
import ErrorMessageIndicator from 'components/widgets/ErrorMessageIndicator';
import { isEmpty } from 'lodash';
import BasicStyles from 'styles/modules/basic/basicStyles';
import clsx from 'clsx';
import { AccountingSubModules } from 'utils/enum/AccountingEnum';
import useGlobalRole from 'components/hook/core/useGlobalRole';
import StringUtils from 'lib/StringUtils';

const useStyle = makeStyles((theme) => AccountingStyles.dialogForm(theme));
const basicFormStyle = makeStyles((theme) => BasicStyles.basicForm(theme));
const ownStyle = makeStyles((theme) => ({
    dialogContent: {
        height: 600,
        padding: theme.spacing(1, 1.8),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '& .gutter.gutter-vertical': {
            margin: '12px',
        },
    },
    toolBarColor: {
        backgroundColor: theme.palette.background.default,
    },
    paddingLeftRigth20: {
        paddingLeft: '20px',
        paddingRight: '20px',
    },
    containerDetail: {
        padding: '15px',
        marginTop: '12px',
        fontSize: '14px',
        '& div': {
            display: 'flex',
            alignItems: 'center',
            marginBlock: '8px',
        },
        '& .form-label': {
            minWidth: 200,
            marginBottom: 0,
        },
    },
    returnPartsTitle: {
        display: 'block',
        padding: '4px 14px 14px',
        fontWeight: 'bold',
    },
    link: {
        textDecoration: 'none',
        '& span': {
            color: theme.palette.text.dodgerBlue,
        },
    },
}));

const keyStore = new KeyStore();

const salesTypesData = [{ type: PartsTicketSalesType.RETAIL }, { type: PartsTicketSalesType.WHOLESALE }, { type: PartsTicketSalesType.WEOWE }];

const PartsTicketCreate = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { employeeTypes, loaded } = useGlobalRole(CompanySettingCategory.PARTS, CompanySettingkey.PARTS_CLERK_GLOBAL_ROLE);

    const { id } = useParams();
    const history = useHistory();

    const PARTS_TICKET_WRITE_EDIT = keyStore.hasPermission(Permission.PARTS_TICKET_WRITE_EDIT);

    const initialState = {
        record: {
            ticketNumber: 0,
            customerCode: 0,
            cellPhone: '',
            email: '',
            customerName: '',
            customerPhone: '',
            customerEmail: '',
            customerAddress: '',
            lotId: 0,
            lotName: '',
            status: PartsTicketStatus.NEW,
            isTaxable: false,
            salesType: PartsTicketSalesType.RETAIL,
            counterPerson: '',
            discountPercent: 0,
            ticketOpen: new Date(),
            ticketClose: null,
            quickNote: '',
            total: 0,
            amountReturn: 0,
            totalInvoice: 0,
            totalCustomerRefund: 0,
        },
        referenceNumber: '',
        cbId: null,
        isDirty: false,
        couldLostData: false,
        showPartsTicketPopup: true,
        clerkList: [],
        openCustomerDialog: !(id > 0),
        openNewCustomerDialog: false,
        partsSettings: {
            surplusTax: 0,
        },
    };

    const classes = {
        ...useStyle(),
        ...basicFormStyle(),
        ...ownStyle(),
    };
    const detailRef = useRef();
    const detailReturnRef = useRef();

    const [state, dispatch] = useReducer(PartsTicketReducer, initialState);
    const {
        record, isDirty, couldLostData,
        showPartsTicketPopup, cbId,
        clerkList, openCustomerDialog,
        openNewCustomerDialog, referenceNumber,
    } = state;

    const { isValid, errors } = isValidSchema(PartsTicketSchema, record);
    const isNew = record.ticketNumber === 0;

    const {
        defaultLotId, defaultLot, lotsUser, availableLots,
    } = useContext(UserContext);
    const overrideAvailableLots = (availableLots || []).map((item) => ({ value: item.id, label: item.label }));

    const { lotName, counterPerson } = record;

    const {
        editCustomer,
        editSalesType,
        editCounterPerson,
        editDiscountPercent,
        editLot,
        editQuickNote,
        editDetails,
        allowSaveAction,
    } = usePartsTicketValidateActions(record.status);

    const [getClerkList] = useLazyQuery(UserQuery.GET_USERS, {
        variables: {
            types: employeeTypes,
        },
        onCompleted: (res) => {
            if (res.getUsers) {
                const list = res?.getUsers?.map((item) => ({
                    userId: item.userId,
                    user: [item.firstName, item.lastName].join(' '),
                }));

                dispatch({
                    type: ACTION_TYPES.SET_RECORD_VALUE,
                    value: {
                        key: 'clerkList',
                        value: list,
                    },
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage([error]);
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [getPartsTicket, { loading }] = useLazyQuery(PartsQuery.GET_PARTS_TICKET, {
        onCompleted: (res) => {
            if (res.getPartsTicket) {
                const result = PartsMapper.mapPartsTicket(res.getPartsTicket);

                if (result.lotName !== '' && (result?.lotId ?? 0) === 0) {
                    const currentLot = lotsUser?.find((c) => c.lotName === record.lotName);
                    result.lotId = currentLot?.lotId ?? 0;
                }

                dispatch({
                    type: ACTION_TYPES.SET_STATE_VALUES,
                    value: {
                        record: { ...result },
                    },
                });
            }
        },
        onError: (error) => {
            ModalUtils.errorMessage([error]);
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const [getCbIdAndCheckNumberByTicketNumber] = useLazyQuery(PartsQuery.GET_CBID_AND_CHECK_NUMBER_BY_TICKET_NUMBER, {
        onCompleted: (res) => {
            if (res.getCbIdAndCheckNumberByTicketNumber) {
                dispatch({
                    type: ACTION_TYPES.SET_CBID_CHECK_NUMBER,
                    value: {
                        cbId: res.getCbIdAndCheckNumberByTicketNumber.cbId,
                        referenceNumber: res.getCbIdAndCheckNumberByTicketNumber.referenceNumber,
                    },
                });
            }
        },
        fetchPolicy: FetchPolicy.NETWORK_ONLY,
    });

    const onClearForm = () => {
        dispatch({
            type: ACTION_TYPES.SET_INITIAL_STATE,
            value: initialState,
        });
    };

    const onCloseAction = () => {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            value: {
                showPartsTicketPopup: false,
                isDirty: false,
            },
        });

        history.push(`/${modules.PARTS}/${PartsPurchaseSubModules.PARTS_TICKET}`);
    };

    const onPopupClose = (event, forceClose = false) => {
        if ((isDirty) && !forceClose) {
            dispatch(
                {
                    type: ACTION_TYPES.SET_ON_POPUP_CLOSE,
                },
            );
            return;
        }

        onClearForm();
        onCloseAction();
    };

    const mutationQuery = isNew ? PartsMutation.CREATE_PARTS_TICKET : PartsMutation.UPDATE_PARTS_TICKET;
    const [addRecord, { loading: isSaving }] = useMutation(mutationQuery, {
        onCompleted: (mutationData) => {
            if (mutationData?.createPartsTicket || mutationData?.updatePartsTicket) {
                ModalUtils.successMessage(null, 'Successfully saved!');
            }
        },
        onError: ({ graphQLErrors }) => {
            ModalUtils.errorMessage(graphQLErrors, null, Array.isArray(graphQLErrors[0]) ? { title: 'Error' } : null);
        },
    });

    const onSave = async (requestedNew) => {
        let resultSave = false;
        let recordToSave = { ...record };

        const { records, errors: detailLinesError } = detailRef.current.getRecords();
        const { records: recordsReturn } = detailReturnRef.current.getRecords();

        if (records.length === 0 && recordsReturn.length === 0) {
            ModalUtils.errorMessage(null, 'The detail lines are empty');
            return;
        }

        if (detailLinesError.length > 0) {
            ModalUtils.errorMessage(detailLinesError);
            return;
        }

        recordToSave.partsTicketDetails = records;
        recordToSave.partsTicketReturnDetails = recordsReturn;
        recordToSave = PartsMapper.mapPartsTicketToSave(recordToSave);

        const resultAfterSave = await addRecord({
            variables: {
                record: recordToSave,
            },
        });

        const createdPartsTicketId = isNew ? resultAfterSave?.data?.createPartsTicket : resultAfterSave?.data?.updatePartsTicket;
        resultSave = createdPartsTicketId > 0;

        if (resultSave) {
            if (!requestedNew) onCloseAction(true);
            else {
                dispatch({
                    type: ACTION_TYPES.SET_SAVE_AND_NEW_PARTS_TICKET,
                    value: initialState,
                });

                detailRef.current.clear();
                detailReturnRef.current.clear();
            }
        }
    };

    const setOpenCustomerDialog = (val) => {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            value: {
                openCustomerDialog: val,
            },
        });
    };

    const setOpenNewCustomerDialog = (val) => {
        dispatch({
            type: ACTION_TYPES.SET_STATE_VALUES,
            value: {
                openNewCustomerDialog: val,
            },
        });
    };

    const onSetCustomer = (val) => {
        const {
            firstName, lastName, customerCode, isTaxable,
            cellPhone, email,
        } = val;

        dispatch({
            type: ACTION_TYPES.SET_RECORD_CHANGED,
            value: {
                ...record,
                customerCode,
                customerName: `${firstName} ${lastName}`,
                customerEmail: email,
                customerPhone: cellPhone,
                isTaxable,
            },
        });
    };

    const clearCustomerInformation = () => {
        setOpenCustomerDialog(false);
        dispatch({
            type: ACTION_TYPES.SET_RECORD_CHANGED,
            value: {
                ...record,
                customerCode: 0,
                customerName: '',
                isTaxable: true,
                customerPhone: '',
                customerEmail: '',
            },
        });
    };

    const onClickPrimaryDialog = (val) => {
        if (val.customer) onSetCustomer(val.customer);
        setOpenCustomerDialog(false);
    };

    const onChangeValue = (field, value) => {
        if (value === record[field]) return;

        const currentRecord = { ...record };

        currentRecord[field] = value;

        dispatch({
            type: ACTION_TYPES.SET_RECORD_CHANGED,
            value: currentRecord,
        });
    };

    const onChangeLot = (field, value) => {
        const currentRecord = { ...record };

        currentRecord.lotId = value;
        const lotNameSelected = overrideAvailableLots?.find((c) => c.value === value);
        currentRecord.lotName = lotNameSelected?.label ?? '';

        dispatch({
            type: ACTION_TYPES.SET_RECORD_CHANGED,
            value: currentRecord,
        });
    };

    const onChangeCounterPerson = (field, value) => {
        const currentRecord = { ...record };

        currentRecord.counterPersonId = value === 0 ? null : value;
        const currentClerk = clerkList?.find((c) => c.userId === value);
        currentRecord.counterPerson = value === 0 ? null : currentClerk.user;

        dispatch({
            type: ACTION_TYPES.SET_RECORD_CHANGED,
            value: currentRecord,
        });
    };

    const getStatusChip = () => {
        if (record?.status.toLowerCase() === PartsTicketStatus.NEW.toLowerCase()) return null;

        let badgeStatus = classes.primary;
        if (record.status.toLowerCase() === PartsTicketStatus.QUOTE.toLowerCase()) badgeStatus = classes.badgeCleared;
        else if (record.status.toLowerCase() === PartsTicketStatus.IN_PROGRESS.toLowerCase()) badgeStatus = classes.badgePrinted;
        else if (record.status.toLowerCase() === PartsTicketStatus.CLOSED.toLowerCase()) badgeStatus = classes.badgeApproved;
        else if (record.status.toLowerCase() === PartsTicketStatus.VOID.toLowerCase()) badgeStatus = classes.badgeVoid;

        return <Chip size="small" label={record.status} className={badgeStatus} />;
    };

    const setInvoiceCustomerRefund = (amount, field) => {
        if (field === 'totalCustomerRefund') {
            const { record: { total } } = state;
            const totalInvoice = Math.max(total - amount, 0);
            const totalCustomerRefund = Math.max(amount - total, 0);

            dispatch({
                type: ACTION_TYPES.SET_CALCULATE_INVOICE,
                value: {
                    amountReturn: amount,
                    totalInvoice,
                    totalCustomerRefund,
                },
            });
        } else if (field === 'totalInvoice') {
            const { record: { amountReturn } } = state;
            const totalInvoice = Math.max(amount - amountReturn, 0);
            const totalCustomerRefund = Math.max(amountReturn - amount, 0);

            dispatch({
                type: ACTION_TYPES.SET_CALCULATE_INVOICE,
                value: {
                    total: amount,
                    totalInvoice,
                    totalCustomerRefund,
                },
            });
        }
    };

    useEffect(() => {
        if (id > 0) {
            getPartsTicket({
                variables: {
                    id: Number(id),
                },
            });
            getCbIdAndCheckNumberByTicketNumber({
                variables: {
                    ticketNumber: Number(id),
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (lotName !== '') {
            if (record.lotId === 0 && overrideAvailableLots.length > 0) {
                const currentLot = overrideAvailableLots.find((c) => c.label.toLowerCase() === lotName.toLowerCase());
                dispatch({
                    type: ACTION_TYPES.SET_RECORD,
                    value: {
                        ...record,
                        lotId: currentLot.value,
                    },
                });
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lotName, overrideAvailableLots.length > 0]);

    useEffect(() => {
        if (defaultLotId > 0 && record.lotName === '') {
            dispatch({
                type: ACTION_TYPES.SET_DEFAULT_LOT,
                value: {
                    lotId: defaultLotId,
                    lotName: defaultLot,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (loaded) getClerkList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded]);

    useEffect(() => {
        if (counterPerson && (counterPerson !== '' && (record.counterPersonId === 0 || isEmpty(record.counterPersonId)) && clerkList.length > 0)) {
            const currentClerk = clerkList.find((c) => c.user.toLowerCase() === counterPerson.toLowerCase());
            dispatch({
                type: ACTION_TYPES.SET_RECORD,
                value: {
                    ...record,
                    counterPersonId: currentClerk?.userId ?? null,
                },
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [counterPerson, clerkList.length > 0]);

    const enableBtnSave = (isValid && isDirty && allowSaveAction && !isSaving);
    const messageToAllowSave = [];

    errors?.forEach((error) => {
        messageToAllowSave.push(error.message);
    });

    return (
        <>
            <Dialog
                open={showPartsTicketPopup}
                onClose={onPopupClose}
                aria-labelledby="form-dialog-title"
                fullScreen
                maxWidth="lg"
                disableBackdropClick
                disableEscapeKeyDown
                scroll="paper"
            >
                <AppBar className={classes.appBar}>
                    <Toolbar className={clsx(classes.centerItems, classes.toolBarColor)}>
                        <div className={classes.buttonSpacing}>
                            {PARTS_TICKET_WRITE_EDIT
                        && allowSaveAction
                                && (
                                    <ErrorMessageIndicator
                                        messages={messageToAllowSave}
                                    >

                                        <ButtonMaterial
                                            className={classes.buttonSave}
                                            startIcon={<SaveIcon />}
                                            size="small"
                                            disabled={!enableBtnSave}
                                            onClick={() => onSave(false)}
                                            variant="contained"
                                            color="secondary"
                                        >
                                            Save
                                        </ButtonMaterial>
                                    </ErrorMessageIndicator>
                                )}
                            {PARTS_TICKET_WRITE_EDIT
                        && allowSaveAction
                                && (
                                    <ButtonMaterial
                                        className={classes.buttonSave}
                                        startIcon={<SaveIcon />}
                                        size="small"
                                        disabled={!enableBtnSave}
                                        onClick={() => onSave(true)}
                                        variant="contained"
                                        color="secondary"
                                    >
                                        Save & New
                                    </ButtonMaterial>
                                )}
                        </div>
                        <div className={classes.centerItems}>
                            <Typography variant="h6">
                                { record.ticketNumber === 0 ? 'New Parts Ticket' : `Edit Parts Ticket ${record.ticketNumber}` }
                            </Typography>
                            <div className={classes.paddingLeftRigth20}>
                                { getStatusChip() }
                            </div>
                            <IconButton edge="start" color="primary" onClick={onPopupClose}>
                                <CloseIcon />
                            </IconButton>
                        </div>
                    </Toolbar>
                </AppBar>
                <DialogContent className={classes.dialogContent}>
                    { loading ? <LoadingMask />
                        : (
                            <Container className={classes.containerSplit}>
                                <Split
                                    sizes={isMobile ? [70, 30] : [30, 70]}
                                    className={isMobile ? classes.verticalSplit : 'split flex-1'}
                                    direction={isMobile ? null : 'horizontal'}
                                >
                                    <div>
                                        <Grid container className={classes.compactForm}>
                                            <Grid item xs={12}>
                                                <Form.Group as={Col}>
                                                    <LabelMemorize text="Customer *" />
                                                    <InputGroup size="sm">
                                                        <Form.Control
                                                            className={isValidField(errors, 'customerCode') ? 'invalid-field' : ''}
                                                            value={record.customerName}
                                                            type="text"
                                                            placeholder="Customer name"
                                                            size="sm"
                                                            disabled
                                                            name="customerName"
                                                        />
                                                        {editCustomer && (
                                                            <InputGroup.Append>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => setOpenCustomerDialog(true)}
                                                                    disabled={!editCustomer}
                                                                >
                                                                    Select
                                                                </Button>
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={clearCustomerInformation}
                                                                    disabled={!editCustomer}
                                                                >
                                                                    X
                                                                </Button>
                                                            </InputGroup.Append>
                                                        ) }
                                                    </InputGroup>
                                                    <LabelMemorize text={`Email: ${record.customerEmail}. Cell phone: ${record.customerPhone}`} />
                                                </Form.Group>
                                            </Grid>
                                            <Grid item xs={isMobile ? 12 : 6}>
                                                <Form.Group as={Col}>
                                                    <LabelMemorize text="Lot *" />
                                                    <DropdownQuery
                                                        name="lotId"
                                                        disabled={!editLot}
                                                        className={isValidField(errors, 'lotId') ? 'invalid-field' : ''}
                                                        value={record.lotId}
                                                        placeHolder="select a lot"
                                                        onChange={(name, newValue) => onChangeLot(name, newValue)}
                                                        dataSource={{
                                                            localData: overrideAvailableLots,
                                                            idField: 'value',
                                                            descriptionField: 'label',
                                                        }}
                                                    />
                                                </Form.Group>
                                                <Form.Group as={Col}>
                                                    <LabelMemorize text="Sales Type *" />
                                                    <DropdownQuery
                                                        name="salesType"
                                                        disabled={!editSalesType}
                                                        className={isValidField(errors, 'salesType') ? 'invalid-field' : ''}
                                                        value={record.salesType}
                                                        placeHolder="select a sales type"
                                                        onChange={(name, newValue) => onChangeValue(name, newValue === 0 ? '' : newValue)}
                                                        dataSource={{
                                                            localData: salesTypesData,
                                                            idField: 'type',
                                                            descriptionField: 'type',
                                                        }}
                                                    />
                                                </Form.Group>
                                            </Grid>
                                            <Grid item xs={isMobile ? 12 : 6}>
                                                <Form.Group as={Col}>
                                                    <LabelMemorize text="Parts Clerk" />
                                                    <DropdownQuery
                                                        name="counterPersonId"
                                                        disabled={!editCounterPerson}
                                                        className={isValidField(errors, 'counterPersonId') ? 'invalid-field' : ''}
                                                        value={record.counterPersonId}
                                                        placeHolder="select a parts clerk"
                                                        onChange={(name, newValue) => onChangeCounterPerson(name, newValue)}
                                                        dataSource={{
                                                            localData: clerkList,
                                                            idField: 'userId',
                                                            descriptionField: 'user',
                                                        }}
                                                        defaultEmptyLineText="Empty"
                                                    />
                                                </Form.Group>
                                                <Form.Group as={Col}>
                                                    <LabelMemorize text="Discount %" />
                                                    <InputNumber
                                                        disabled={!editDiscountPercent}
                                                        size="sm"
                                                        showCurrency={false}
                                                        allowNegative={false}
                                                        value={record.discountPercent}
                                                        thousandSeparator
                                                        placeholder="0"
                                                        onChange={(value) => onChangeValue('discountPercent', value)}
                                                    />
                                                </Form.Group>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Form.Group as={Col}>
                                                    <LabelMemorize text="Note" />
                                                    <Form.Control
                                                        as="textarea"
                                                        disabled={!editQuickNote}
                                                        className={isValidField(errors, 'quickNote') ? 'invalid-field' : ''}
                                                        size="sm"
                                                        rows="2"
                                                        value={record.quickNote || ''}
                                                        placeholder="Notes"
                                                        onChange={(e) => onChangeValue('quickNote', e.target.value)}
                                                    />
                                                    <p className={classes.fontSmall}>{getErrorMessage('quickNote', errors)}</p>
                                                </Form.Group>
                                            </Grid>
                                        </Grid>
                                        <div className={classes.containerDetail}>
                                            <div>
                                                <LabelMemorize text="Total Invoice:" />
                                                <InputNumber
                                                    disabled
                                                    size="sm"
                                                    showCurrency
                                                    fixedDecimalScale
                                                    allowNegative={false}
                                                    value={record.totalInvoice}
                                                />
                                            </div>
                                            <div>
                                                <LabelMemorize text="Total Customer Refund:" />
                                                <InputNumber
                                                    disabled
                                                    size="sm"
                                                    showCurrency
                                                    fixedDecimalScale
                                                    allowNegative={false}
                                                    value={record.totalCustomerRefund}
                                                />
                                            </div>
                                            {cbId > 0 && !StringUtils.isEmpty(referenceNumber) && (
                                                <div>
                                                    <LabelMemorize text="" />
                                                    <RouteLink
                                                        to={{
                                                            pathname: `/${modules.ACCOUNTING}/${AccountingSubModules.BANKING}/check/${cbId}`,
                                                        }}
                                                        target="_blank"
                                                        className="link"
                                                    >
                                                        {`Check #${referenceNumber}`}
                                                    </RouteLink>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <Split
                                        sizes={[50, 30]}
                                        direction="vertical"
                                    >
                                        <PartsTicketDetail
                                            lotName={record.lotName}
                                            recordId={record.ticketNumber}
                                            discountPercent={record.discountPercent ?? 0}
                                            recordFields={{
                                                isTaxable: record.isTaxable ?? true,
                                                sumSubTotal: (record.subTotal || record.totalNetPrice) ?? 0,
                                                sumDiscount: record.discount ?? 0,
                                                sumTax: record.totalTax ?? 0,
                                                sumTotal: record.total ?? 0,
                                            }}
                                            editDetails={editDetails}
                                            writePermissions={PARTS_TICKET_WRITE_EDIT}
                                            ref={detailRef}
                                            includeOnlyNotReturned
                                            onDirty={() => dispatch({ type: ACTION_TYPES.SET_STATE_VALUES, value: { isDirty: true } })}
                                            setInvoiceCustomerRefund={setInvoiceCustomerRefund}
                                        />
                                        <div>
                                            <span className={classes.returnPartsTitle}>Return parts</span>
                                            <PartsTicketDetail
                                                lotName={record.lotName}
                                                recordId={record.ticketNumber}
                                                discountPercent={record.discountPercent ?? 0}
                                                customerCode={record.customerCode}
                                                editDetails={editDetails}
                                                writePermissions={PARTS_TICKET_WRITE_EDIT}
                                                ref={detailReturnRef}
                                                isReturnPart
                                                includeOnlyReturned
                                                onDirty={() => dispatch({ type: ACTION_TYPES.SET_STATE_VALUES, value: { isDirty: true } })}
                                                setInvoiceCustomerRefund={setInvoiceCustomerRefund}
                                            />
                                        </div>
                                    </Split>
                                </Split>
                                <ConfirmDialog
                                    title="Attention - You have unsaved changes!"
                                    description="Do you want to close without saving?"
                                    open={couldLostData}
                                    variant="outlined"
                                    titlePrimary="Close"
                                    titleSecondary="Cancel"
                                    onClose={() => dispatch({ type: ACTION_TYPES.SET_RECORD_VALUE, value: { key: 'couldLostData', value: false } })}
                                    onClickSecondary={() => dispatch({ type: ACTION_TYPES.SET_RECORD_VALUE, value: { key: 'couldLostData', value: false } })}
                                    onClickPrimary={(e) => onPopupClose(e, true)}
                                />
                            </Container>
                        )}
                </DialogContent>
                {isSaving && <DialogActionMessage message="Saving information... " />}
            </Dialog>
            <If condition={openCustomerDialog}>
                <CustomerForServiceList
                    inHouse={false}
                    showVehicleGrid={false}
                    onClose={() => setOpenCustomerDialog(false)}
                    onSelectCustomer={onClickPrimaryDialog}
                    showAddNewCustomerButton
                    onClickNewCustomerButton={() => { setOpenCustomerDialog(false); setOpenNewCustomerDialog(true); }}
                />
            </If>
            <If condition={openNewCustomerDialog}>
                <Dialog
                    open={openNewCustomerDialog}
                    fullWidth
                    maxWidth="lg"
                >
                    <DialogAppBar
                        title="Create new customer"
                        onClose={() => setOpenNewCustomerDialog(false)}
                    />
                    <DialogContent className={classes.dialogContent}>
                        <CreateCustomer toggleCreateCustomer={() => setOpenNewCustomerDialog(false)} onSetCustomer={onSetCustomer} />
                    </DialogContent>
                </Dialog>
            </If>
        </>
    );
};

export default PartsTicketCreate;
